import { useState } from 'react'
import { useDispatch } from 'react-redux'

import { showErrorToast, showSuccessToast } from 'app/appSlice'

import { AIEditFlyoutState } from 'api/interfaces/AIEditTypes'

interface AIEditMutationArgs {
  entityId: string
  targetType: string
  targetId: string
  prompt: string
  sectionName?: string
  fieldKey?: string
  campaignBuilderId?: string
  constraintCampaignType?: string
  constraintStartDate?: string
  constraintDuration?: string
  constraintBudget?: string
  offAdCritterPlatformName?: string
  deepThink?: boolean
  execSummarySections?: Record<string, { title: string; content: string }>
}

type AIEditMutationFn = (args: AIEditMutationArgs) => { unwrap: () => Promise<unknown> }

interface UseAIEditOptions {
  mutationFn: AIEditMutationFn
  entityId: string
  onSuccess?: () => void
}

export const useAIEdit = ({ mutationFn, entityId, onSuccess }: UseAIEditOptions) => {
  const dispatch = useDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const submit = async (prompt: string, flyoutState: AIEditFlyoutState, deepThink: boolean = false) => {
    setIsSubmitting(true)
    try {
      await mutationFn({
        entityId,
        targetType: flyoutState.targetType,
        targetId: flyoutState.targetId,
        prompt,
        sectionName: flyoutState.sectionName,
        fieldKey: flyoutState.fieldKey,
        campaignBuilderId: flyoutState.campaignBuilderId,
        constraintCampaignType: flyoutState.constraintCampaignType,
        constraintStartDate: flyoutState.constraintStartDate,
        constraintDuration: flyoutState.constraintDuration,
        constraintBudget: flyoutState.constraintBudget,
        offAdCritterPlatformName: flyoutState.offAdCritterPlatformName,
        deepThink,
        execSummarySections: flyoutState.execSummarySections
      }).unwrap()

      dispatch(showSuccessToast('AI edit applied successfully'))
      onSuccess?.()
    } catch (error: unknown) {
      const apiError = error as { status?: number }
      if (apiError?.status === 409) {
        dispatch(showErrorToast('Another edit is in progress on a related section. Please try again shortly.'))
      } else {
        dispatch(showErrorToast('AI edit failed. Please try again.'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return { submit, isSubmitting }
}
